<template>
  <v-tabs id="fixed-tabs" color="primary" v-model="activeTabId">
    <v-tab>General</v-tab>
    <v-tab @click="onProgramsTabClick">Programs</v-tab>
    <v-tab @click="getHistoryData">History</v-tab>
    <v-tab>Caps</v-tab>
    <v-tab @click="getCampaignDetails">Settings</v-tab>
    <v-tab @click="getPostbacks">Postback</v-tab>
    <v-tab-item :key="`campaign-${item.data.id || item.data.advertiser_id}-general`">
      <v-container fluid style="padding-top: 50px">

        <v-row no-gutters>
          <v-col sm="5" md="5" lg="5">
            <v-text-field label="Name" hide-details v-model="name"></v-text-field>
          </v-col>
          <v-col sm="5" md="5" lg="5" offset-md="2">
            <v-text-field label="Currency"  disabled hide-details :value="item.data.currencyName"></v-text-field>
          </v-col>
        </v-row>

        <v-row no-gutters class="mt-5">
          <v-col sm="5" md="5" lg="5">
            <v-text-field label="Affiliate" disabled hide-details :value="item.data.affNameId"></v-text-field>
          </v-col>
          <v-col sm="5" md="5" lg="5" offset-md="2">
            <v-row class="align-end ma-0">
              <v-text-field
                  label="Active Program"
                  disabled
                  hide-details
                  :value="activeProgram ? activeProgram.name : 'No Program'"
              />
              <v-btn color="primary" :disabled="false" text x-small @click="redirectToProgramsTab">
                <v-icon>
                  mdi-tag
                </v-icon>
              </v-btn>
            </v-row>
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col sm="5" md="5" lg="5">
            <v-text-field label="Offer" disabled hide-details :value="item.data.offerName"></v-text-field>
          </v-col>
          <v-col sm="5" md="5" lg="5" offset-md="2">
            <v-select hide-details label="Status"
                      ref="status"
                      :items="[{value: 'active', text: 'Active'}, {value: 'inactive', text: 'Inactive'}, {value: 'blocked', text: 'Blocked'}, {value: 'rejected', text: 'Rejected'}]"
                      v-model="status"
            ></v-select>
          </v-col>
        </v-row>
        <v-btn
                v-if="!checkInputsNotChanged"
                fixed
                top
                right
                style="top: 50px; z-index: 2"
                color="primary"
                class="upd-btn ma-0"
                :loading="updating"
                :disabled="updating"
                @click="onUpdate"
        >
          Update
        </v-btn>
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field label="Postback" hide-details v-model="postbackUrl"></v-text-field>
          </v-col>
        </v-row>
        <v-row no-gutters class="mt-3">
          <v-col cols="4" md="3" class="pb-0">
            <v-text-field
                label="Affiliate Multi Pings"
                type="number"
                min="1"
                hide-details
                clearable
                v-model.number="affiliateMultiPings"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-5" no-gutters>
          <v-col cols="4" md="3" class="pb-0">
            <v-select hide-details label="Trends" v-model="chartOptions.chartPeriod" :items="[{value: 7, text: 'Last 7 Days'}]"></v-select>
          </v-col>
          <v-col cols="3" />
          <v-col cols="5" class="pb-0" v-if="seePingRate">
            <v-text-field
                :disabled="!editPingRate"
                :rules="pingRateRules"
                label="Ping Rate"
                append-icon="mdi-percent-outline"
                v-model="pingRate"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-skeleton-loader :loading="chartLoading" class="mx-auto mt-3" width="100%" height="250px" type="image">
              <apexchart type="line" height="250px" :options="chartOptions"  :series="series"></apexchart>
            </v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
    <v-tab-item>
      <v-container fluid style="padding-top: 50px">
        <v-row no-gutters>
          <v-col cols="5">
            <v-autocomplete
                hide-details
                :disabled="!user[0].can('assign_programs')"
                label="Available Program"
                v-model="activeProgramId"
                :items="programsPrepared"
                placeholder="Start typing to Search"
            />
          </v-col>
          <v-col cols="1" class="download">
            <span class="mr-3 primary-color" @click="download(activeProgramId)">
              <i class="fas fa-download"></i>
            </span>
          </v-col>
          <v-spacer />
          <v-col cols="5" class="d-flex align-center justify-end text-right">
            <a v-if="user[0].can('create_programs')" @click="createProgramRedirect" class="new-program">
              <b>Need a new program?</b>
              <v-icon>mdi-open-in-new</v-icon>
            </a>
          </v-col>
          <v-col cols="12">
            <v-simple-table v-if="!isProgramLoading">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">
                      Code
                    </th>
                    <th class="text-center">
                      Geo
                    </th>
                    <th class="text-center">
                      Payout (USD)
                    </th>
                    <th v-if="user[0].can('see_profit')" class="text-center">
                      Price (USD)
                    </th>
                    <th v-if="user[0].can('see_profit')" class="text-center">
                      Margin
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(program, index) in preparedProgramsForTable"
                    :key="index"
                  >
                    <td class="text-center">{{ program.geo }}</td>
                    <td class="text-center">{{ program.device_type ? `${program.countryName} (${program.device_type})` : program.countryName }}</td>
                    <td class="text-center">{{ program.payout }}</td>
                    <td v-if="user[0].can('see_profit')" class="text-center">{{ program.price }}</td>
                    <td v-if="user[0].can('see_profit')" class="text-center">{{ program.margin }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div style="text-align: center; margin-top: 10%" v-else>
              <v-progress-circular
                      :size="50"
                      color="primary"
                      indeterminate
              >
              </v-progress-circular>
            </div>

          </v-col>

        </v-row>
        <v-btn
            v-if="programUpdateButtonVisible"
            fixed
            top
            right
            style="top: 50px; z-index: 2"
            color="primary"
            class="upd-btn ma-0"
            @click="updateProgram(item.data)"
        >
          Update
        </v-btn>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`campaign-${item.data.id}-history`" style="padding-top: 50px">
      <v-data-table
          :headers="historyHeaders"
          :items="historyData"
          :loading="isLogsLoading"
          fixed-header
          loading-text="Loading... Please wait"
          color="#7F98A5"
      >
        <template v-slot:item.logs="{ item }" >
          <HistoryLog :logsString='item.logs' ></HistoryLog>
        </template>
      </v-data-table>
    </v-tab-item>
    <v-tab-item :key="`campaign-${item.data.id}-caps`">
      <v-container fluid style="padding-top: 50px">
        <v-row align="center">
          <v-col cols="12" sm="12" class="py-0">


            <v-checkbox
                    v-model="caps.isCapsEnabled"
                    label="Caps enabled"
                    color="orange darken-3"
                    hide-details
            ></v-checkbox>
            <div v-if="caps.isCapsEnabled" class="">
              <v-container>
                <v-row align="center" justify-md="start">
                  <v-checkbox v-model="caps.useDates" hide-details label="Use dates"></v-checkbox>
                </v-row>
                <v-row align="center" justify-md="start" v-if="caps.useDates">
                  <v-col md="6">
                    <v-menu
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                                label="Start date"
                                readonly
                                :value="caps.startDate"
                                v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                              locale="en-in"
                              no-title
                              @input="updStart"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col md="6">
                    <v-menu
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                                label="End date"
                                readonly
                                :value="caps.endDate"
                                v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                              locale="en-in"
                              no-title
                              @input="updEnd"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12"><h6>Sales</h6></v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Day" v-model="caps.salesDay"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Week" v-model="caps.salesWeek"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Month" v-model="caps.salesMonth"></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12">
                    <custom-select
                      :options="this.getExitTrafficOffers"
                      label="nameId"
                      @search="exitTrafficOffersUpd"
                      v-model="caps.salesRedirectOfferId"
                      :filterable="false"
                      :clearable="false"
                      class="custom_select"
                    />
                  </v-col>
                </v-row>

                <v-divider class="divider"></v-divider>

                <v-row align="center" justify="start">
                  <v-col md="12"><h6>Clicks</h6></v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Day" v-model="caps.clicksDay"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Week" v-model="caps.clicksWeek"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Month" v-model="caps.clicksMonth"></v-text-field>
                  </v-col>

                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12">
                    <custom-select
                      :options="this.getExitTrafficOffers"
                      label="nameId"
                      @search="exitTrafficOffersUpd"
                      v-model="caps.clicksRedirectOfferId"
                      :filterable="false"
                      :clearable="false"
                      class="custom_select"
                      append-to-body
                    />
                  </v-col>
                </v-row>
              </v-container>
            </div>
            <div class="">
              <v-btn
                      fixed
                      top
                      right
                      style="top: 50px;z-index: 2"
                      color="primary"
                      class="upd-btn ma-0"
                      @click="updateCaps"
              >
                Update
              </v-btn>
            </div>

          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`campaign-${item.data.id}-settings`">
      <div class="flex row jccenter">
        <v-progress-circular
                class="progress-absolute"
                v-if="isCampaignDetailsLoading"
                indeterminate
                color="primary"
                size="100"
                style="margin-top: 20%"
        ></v-progress-circular>
        <div v-else style="padding-top: 50px; padding-left:40px; min-width:500px;min-height:500px;">
          <div style="margin-top:10px; margin-bottom: 20px;">
            Custom Options
            <div class="custom-options-list mb-4">
              <v-row v-for="(option, index) in customOptionsLocal" :key="index" class="mt-1">
                <v-col cols="5">
                  <v-select
                    v-model="option.optionName"
                    :items="customOptions.map(opt => opt.optionName).filter((value, index, self) => self.indexOf(value) === index)"
                    label="Option Name"
                    @change="onOptionNameChange(option, index)"
                  />
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-if="option.optionName && hasDuplicateOptionName(option.optionName)"
                    v-model="option.optionValue"
                    :items="getOptionValues(option.optionName)"
                    label="Option Value"
                    @change="onOptionValueChange(option, index)"
                  />
                  <v-text-field
                    v-else
                    v-model="option.optionValue"
                    label="Option Value"
                    @input="onOptionValueChange(option, index)"
                  />
                </v-col>
<!--                <v-col cols="2">-->
<!--                  <v-text-field-->
<!--                    v-model="option.availableProgramIds"-->
<!--                    label="Programs"-->
<!--                    readonly-->
<!--                  />-->
<!--                </v-col>-->
                <v-col cols="1">
                  <v-btn
                    icon
                    color="primary"
                    style="margin-top: 20px"
                    @click="deleteCustomOption(option)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-row v-if="newCustomOption" class="mt-2">
            <v-col cols="5">
              <v-select
                v-model="newCustomOption.optionName"
                :items="customOptions.map(opt => opt.optionName).filter((value, index, self) => self.indexOf(value) === index)"
                label="Option Name"
              />
            </v-col>
            <v-col cols="5">
              <v-select
                v-if="newCustomOption.optionName && hasDuplicateOptionName(newCustomOption.optionName)"
                v-model="newCustomOption.optionValue"
                :items="getOptionValues(newCustomOption.optionName)"
                label="Option Value"
              />
              <v-text-field
                v-else
                v-model="newCustomOption.optionValue"
                label="Option Value"
              />
            </v-col>
        
          </v-row>
            <div>
              <v-btn
                color="primary"
                class="ml-4"
                @click="addNewCustomOption"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Custom Option
              </v-btn>
            </div> 
          </div>
          <span>Variant</span>
          <custom-select
                  aria-placeholder="Select advertiser first"
                  v-model="currentVariant"
                  :options="getOfferVariants"
                  label="name"
                  class="custom_select"
                  :clearable="false"
                  item-text="name"
                  item-value="id"
          >
          </custom-select>
          <v-img
            v-if=" currentVariant && currentVariant.preSignedUrl"
            :src="currentVariant && currentVariant.preSignedUrl"
          >
          </v-img>
          <v-btn
                  :v-if="true"
                  fixed
                  top
                  right
                  style="top: 50px; z-index: 2"
                  color="primary"
                  class="upd-btn ma-0"
                  @click="getParams"
          >
            <span v-if="!updating">Update</span>
            <v-progress-circular
                    class="progress-absolute"
                    v-else
                    indeterminate
                    color="white"
                    size="30"
            ></v-progress-circular>
          </v-btn>
          <div class="extra">
            <ExtraParamsComponent ref="extraParamsComponent" :extraParams="extraParams" :currentVariant="currentVariant" :isInit="false"/>
          </div>
        </div>
      </div>

    </v-tab-item>
    <v-tab-item :key="`campaign-${item.data.id}-postback`">
      <div class="w-75 ml-10 mt-15">
        <v-progress-circular
                class="progress-absolute"
                v-if="isCampaignPostbackLoading"
                indeterminate
                color="primary"
                size="100"
                style="margin-top: 10%; margin-left: 50%"
        ></v-progress-circular>
        <div v-else>
          <!-- Paid Conversions -->
          <div>
            <div class="title">Paid Conversions</div>
            <div v-for="(conversion, index) in paidConversions" :key="'paid-' + index" class="conversion-item">
              <v-row>
                <v-col cols="8" sm="8">
                  <v-text-field v-model="conversion.postback">
                    <template v-slot:label>
                      <span class="postback-label">Postback {{ index + 1 }}</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="1" sm="1" @click="removePaid(index)" class="remove">
                  <v-icon size="24">mdi-delete</v-icon>
                </v-col>
              </v-row>
            </div>
            <v-btn @click="addPaid" class="mt-3 pl-1 pr-1" color="primary">
              <v-icon>mdi-plus</v-icon>
              ADD POSTBACK
            </v-btn>
          </div>
          <v-divider class="mt-10 mb-10"></v-divider>
          <!-- Secondary Conversions -->
          <div>
            <div class="title">Secondary Conversions</div>
            <div v-for="(conversion, index) in secondaryConversions" :key="'secondary-' + index" class="conversion-item">
              <v-row>
                <v-col cols="6" sm="6">
                  <v-text-field v-model="conversion.postback">
                    <template v-slot:label>
                      <span class="postback-label">Postback {{ index + 1 }}</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="2" sm="2">
                  <v-select
                          v-model="conversion.event"
                          :items="['click', 'conversion', 'installation', 'registration', 'sale']"
                  >
                    <template v-slot:label>
                      <span class="select-label">Event</span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="1" sm="1" @click="removeSecondary(index)" class="remove">
                    <v-icon size="24">mdi-delete</v-icon>
                </v-col>
              </v-row>
            </div>
            <v-btn @click="addSecondary" class="mt-3 pl-1 pr-1" color="primary">
            <v-icon>mdi-plus</v-icon>
            ADD POSTBACK
            </v-btn>
          </div>
        </div>
      </div>
      <div class="">
        <v-btn
                fixed
                top
                right
                style="top: 50px;z-index: 2"
                color="primary"
                class="upd-btn ma-0"
                @click="updatePostbacks"
        >
          <v-progress-circular
                  class="progress-absolute"
                  v-if="isUpdating"
                  indeterminate
                  color="white"
                  size="30"
          ></v-progress-circular>
          <span v-else>Update</span>
        </v-btn>
      </div>
    </v-tab-item>
  </v-tabs>

</template>

<script>
import {mapGetters, mapMutations, mapState} from "vuex";
import reports from "@/api/reports";
import { DateTime } from "luxon";
import store from '@/store';
import { createPeriod } from "@/helpers";
import {getCookie} from "@/helpers";
import programs from '../../../../api/programs';
import campaigns from '@/api/campaigns';
import affiliates from "@/api/affiliates";
import offerCustomOptionsApi from '@/api/offerCustomOptions';
import {api} from "../../../../api/requestGraphql";
import HistoryLog from "@/components/common/HistoryLog";
import ExtraParamsComponent from "@/components/common/CampaignParams.vue";
import {debounce} from "debounce";
import download from "@/helpers/download";

  export default {
    props: {
      item: {
        type: Object
      }
    },
    components: {
      HistoryLog,
      ExtraParamsComponent
    },
    async mounted() {
      await this.getCampaignDetails();
      this.programList = await programs.getProgramsList();
      const allowedCountries = await programs.getAllowedCountriesByOfferId(this.item.data.offerId);

      this.allowedCountries = allowedCountries.countries || [];
      this.isAllCountriesAllowed = allowedCountries.isAllCountriesAllowed;
      await this.fetchProgramData();
      await this.fetchPingRate();
      await this.getCampaignSales();
      await this.$store.dispatch('offer/saveExitTrafficOffersStore')
      this.isMounted = true;

      if (!this.getOffers.data || !this.getOffers.data.length) {
        await store.dispatch('offers/saveOffersStore', {
          filters: {},
          token: getCookie('accessToken')
        })
      }

      this.offers = this.getOffers.data.map(offer => {
        if (offer.status !== 'inactive') {
          return {value: offer.id, text: `(${offer.id}) ${offer.name}`}
        }
      }).filter(offer => offer);

      if (this.caps.salesRedirectOfferId) {
        const matchedOffer = this.getExitTrafficOffers.find(
          offer => offer.id === this.caps.salesRedirectOfferId
        );
        this.caps.salesRedirectOfferId = matchedOffer || this.caps.salesRedirectOfferId;
      }
      if (this.caps.clicksRedirectOfferId) {
        const matchedClickOffer = this.getExitTrafficOffers.find(
          offer => offer.id === this.caps.clicksRedirectOfferId
        );
        this.caps.clicksRedirectOfferId = matchedClickOffer || this.caps.clicksRedirectOfferId;
      }
    },
    computed: {
      ...mapState('user', ['user']),
      ...mapGetters('offers', ['getOffers']),
      ...mapGetters('offer', ['getOfferVariants','getExitTrafficOffers']),
      valid(){
        if(this.isMounted) return true
      },
      seePingRate() {
        return !!this.user[0].can('see_campaign_ping_rate')
      },
      editPingRate() {
        return !!this.user[0].can('edit_campaign_ping_rate')
      },
      checkInputsNotChanged() {
        return this.status === this.item.data.status
                && this.name === this.item.data.name
                && this.pingRate === this.pingRateCached
                && this.postbackUrl === this.item.data.postbackUrl
                && this.affiliateMultiPings === this.item.data.affiliateMultiPings
      },
      programsPrepared() {
        const hasAllowed = Array.isArray(this.allowedPrograms) && this.allowedPrograms.length > 0;
        const allowedSet = hasAllowed ? new Set(this.allowedPrograms.map(id => Number(id))) : null;

        const base = hasAllowed
                ? this.programList.filter(program => allowedSet.has(Number(program.id)))
                : this.programList;

        const tempProgramList = JSON.parse(JSON.stringify(base));
        tempProgramList.map(program => {
          program['text'] = `${program.name} (${program.id})`;
          program['value'] = program.id;
        });
        return tempProgramList;
      },
      preparedProgramsForTable() {
        const preparedProgramsForTable = [...this.programDetails];

        // Sort: group by geo, base records (device_type=null) first, then device overrides
        // Put WW entries first, then sort rest alphabetically
        preparedProgramsForTable.sort((a, b) => {
          const aIsWW = a.geo.toUpperCase() === 'WW';
          const bIsWW = b.geo.toUpperCase() === 'WW';
          if (aIsWW && !bIsWW) return -1;
          if (!aIsWW && bIsWW) return 1;
          // Compare geo codes
          if (a.geo !== b.geo) {
            return a.geo.localeCompare(b.geo);
          }
          // Within same geo: null device_type first, then alphabetically
          if (!a.device_type && b.device_type) return -1;
          if (a.device_type && !b.device_type) return 1;
          if (a.device_type && b.device_type) {
            return a.device_type.localeCompare(b.device_type);
          }
          return 0;
        });

        const activeProgramData = this.programList.find(value => value.id === this.activeProgramId) || {};

        const marginPercent = activeProgramData.payout === 0 ? 0
                : ((1 - (activeProgramData.payout / activeProgramData.price)) * 100).toFixed(2);

        const defaultData = {
          countryName: 'Worldwide (Default)',
          geo: 'WW',
          price: activeProgramData.price || 0,
          payout: activeProgramData.payout || 0,
          margin: `${(activeProgramData.price - activeProgramData.payout).toFixed(2)} (${marginPercent}%)`,
          nextGeo: '',
          nextCode: '',
          nextPrice: '',
          nextPayout: '',
          nextMargin: ''
        };

        if (activeProgramData.payout > 0){
          preparedProgramsForTable.unshift(defaultData);
        }

        return preparedProgramsForTable;
      },
      programUpdateButtonVisible() {
        return !this.activeProgram || this.activeProgram.id !== this.activeProgramId;
      },
      hasDuplicateOptionName() {
        return (optionName) => {
          return this.customOptions.filter(opt => opt.optionName === optionName).length >= 1;
        }
      },
      getOptionValues() {
        return (optionName) => {
          return this.customOptions
            .filter(opt => opt.optionName === optionName)
            .map(opt => opt.optionValue);
        }
      }
    },
    watch: {
      async activeProgramId() {
        if (!this.activeProgramId || isNaN(this.activeProgramId)) return;
        const allowCountries = this.allowedCountries
        this.isProgramLoading = true;
        const programDetails = await programs.getProgramDetails(this.activeProgramId);
        this.isProgramLoading = false;
        if (allowCountries.length !== 0 && !this.isAllCountriesAllowed) {
          this.programDetails = programDetails.filter(detail => allowCountries.includes(detail.geo));
        } else {
          this.programDetails = programDetails;
        }
        this.tempIndex = 0;
        this.$forceUpdate();
      },
      extraParams(newVal, oldVal) {
        console.log('extraParams changed in parent', newVal);
      },
      'newCustomOption': {
        deep: true,
        handler(newVal) {
          if (newVal && newVal.optionName && newVal.optionValue) {
            const match = this.customOptions.find(
                    opt => opt.optionName === newVal.optionName && opt.optionValue === newVal.optionValue
            );
            const availableProgramIds = match && match.availableProgramIds != null
                    ? String(match.availableProgramIds)
                    : '';
            this.customOptionsLocal.push({
              optionName: newVal.optionName,
              optionValue: newVal.optionValue,
              availableProgramIds: availableProgramIds
            });
            this.newCustomOption = null;
          }
        }
      },
      customOptionsLocal: {
        deep: true,
        handler() {
          // Only update if component is mounted to avoid initialization issues
          if (this.isMounted) {
            this.$nextTick(() => {
              this.updateAllowedPrograms();
            });
          }
        }
      },
      activeTabId: {
        handler(newTabId) {
          // If switching to Programs tab (index 1), immediately update allowed programs
          if (newTabId === 1 && this.isMounted) {
            this.$nextTick(() => {
              this.updateAllowedPrograms();
            });
          }
        }
      }
    },
    methods: {
      async exitTrafficOffersUpd(event) {
        this.debounceUpdateExitOffers(event)
      },

      onOptionNameChange(option, index) {
        // Reset option value when option name changes
        option.optionValue = '';
        option.availableProgramIds = '';
        this.updateOptionPrograms(option, index);
      },

      onOptionValueChange(option, index) {
        this.updateOptionPrograms(option, index);
      },

      updateOptionPrograms(option, index) {
        if (option.optionName && option.optionValue) {
          const match = this.customOptions.find(
            opt => opt.optionName === option.optionName && opt.optionValue === option.optionValue
          );
          option.availableProgramIds = match && match.availableProgramIds != null
            ? String(match.availableProgramIds)
            : '';
        }
      },

      deleteCustomOption(optionToDelete) {
        const index = this.customOptionsLocal.findIndex(
          option => option.optionName === optionToDelete.optionName && 
                    option.optionValue === optionToDelete.optionValue
        );
        if (index !== -1) {
          this.customOptionsLocal.splice(index, 1);
        }
      },

      getCustomOptionsIds() {
        return this.customOptionsLocal.map(localOption => {
          const foundOption = this.customOptions.find(
            opt => opt.optionName === localOption.optionName && 
                  opt.optionValue === localOption.optionValue
          );
          return foundOption ? foundOption.id : null;
        }).filter(id => id !== null);
      },

      updateAllowedPrograms() {
        // Convert customOptionsLocal to the format expected by extractProgramIdsFromCustomOptions
        const customOptionsData = this.customOptionsLocal.map(opt => ({
          name: opt.optionName,
          value: opt.optionValue,
          programs: opt.availableProgramIds
        }));
        
        // Update allowed programs based on current custom options
        if (customOptionsData.length === 0) {
          // If no custom options exist, reset to allow all programs
          this.allowedPrograms = [];
        } else {
          // Extract programs from custom options
          this.allowedPrograms = this.extractProgramIdsFromCustomOptions(customOptionsData);
        }
        
        // Force Vue to update the computed properties immediately
        this.$forceUpdate();
        
        // Clear program details if current program is no longer allowed
        // If allowedPrograms is empty, all programs are allowed
        const isProgramAllowed = this.allowedPrograms.length === 0 || this.allowedPrograms.includes(this.activeProgramId);
        if (this.activeProgramId && !isProgramAllowed) {
          this.programDetails = [];
          this.activeProgramId = null;
          this.activeProgram = null;
          
          // Auto-select first available program if any exist
          if (this.allowedPrograms.length > 0) {
            const firstAllowedProgram = this.programList.find(program => 
              this.allowedPrograms.includes(program.id)
            );
            if (firstAllowedProgram) {
              this.activeProgramId = firstAllowedProgram.id;
              this.activeProgram = firstAllowedProgram;
            }
          }
        }
      },

      onProgramsTabClick() {
        // Immediately update allowed programs when switching to Programs tab
        this.updateAllowedPrograms();
      },

      debounceUpdateExitOffers: debounce(async (search) => {
        await store.dispatch('offer/saveExitTrafficOffersStore', {search})
      }, 700),
      createProgramRedirect() {
        this.$router.push('/advertisers/programs/create')

        store.dispatch('cards/MINIMIZE', `${this.item.key}`)
      },
      async fetchProgramData() {
        const programData = await programs.getProgramByCampaign(this.item.data.id);
        const activeProgramId = programData.programOverride ? programData.programOverride : programData.programId;
        const result = this.programList.find(value => value.id === activeProgramId);
        this.activeProgramId = result ? result.id : 1;
        this.activeProgram = result;
        // Store whether this campaign has a program override
        this.hasProgramOverride = !!programData.programOverride;
      },
      async fetchPingRate() {
        const result = await campaigns.getPingRate(this.item.data.id);
        this.pingRate = result === null ? 100 : result;
        this.pingRateCached = this.pingRate;
      },
      async getHistoryData() {
        this.isLogsLoading = true;
        const result = await campaigns.campaignHistory(this.item.data.id);
        this.historyData = result.map(data => ({
          ...data, dateAdded: (new Date(data.dateAdded*1000)).toLocaleDateString("en-US")
        }))
        this.isLogsLoading = false;
      },
      redirectToProgramsTab () {
        this.activeTabId = 1;
        // Immediately update allowed programs when redirecting to Programs tab
        this.$nextTick(() => {
          this.updateAllowedPrograms();
        });
      },
      prepareProgramData(index, program = {}) {
        if (index === 0) {
          this.tempIndex = 0;

          let activeProgramData = {};
          this.programList.forEach(value => {
            if (value.id === this.activeProgramId) {
              activeProgramData = value;
            }
          });

          let marginPercent = activeProgramData.marginPercent;
          if (activeProgramData.marginType === 'fixed') {
            marginPercent = activeProgramData.payout === 0
                ? 0
                : Math.round((1-activeProgramData.payout / activeProgramData.price) * 100);
          }
          if (activeProgramData.payout > 0) {
            const defaultData = {
              code: 'WW',
              geo: 'Worldwide (Default)',
              price: activeProgramData.price,
              payout: activeProgramData.payout,
              margin: `${Math.round((activeProgramData.price - activeProgramData.payout) * 100) / 100} (${marginPercent}%)`,
              nextGeo: this.programDetails.length > 0 ? this.programDetails[this.tempIndex].countryName : '',
              nextCode: this.programDetails.length > 0 ? this.programDetails[this.tempIndex].geo : '',
              nextPrice: this.programDetails.length > 0 ? this.programDetails[this.tempIndex].price : '',
              nextPayout: this.programDetails.length > 0 ? this.programDetails[this.tempIndex].payout : '',
              nextMargin: this.programDetails.length > 0 ? this.programDetails[this.tempIndex].margin : ''
            };

            this.tempIndex++;
            return defaultData;
          }

        }

        const data = {};

        if (this.tempIndex < this.programDetails.length) {
          data['geo'] = this.programDetails[this.tempIndex].countryName;
          data['code'] = this.programDetails[this.tempIndex].geo;
          data['price'] = this.programDetails[this.tempIndex].price;
          data['payout'] = this.programDetails[this.tempIndex].payout;
          data['margin'] = this.programDetails[this.tempIndex].margin;
          this.tempIndex++;
        }

        if (this.tempIndex < this.programDetails.length) {
          data['nextGeo'] = this.programDetails[this.tempIndex].countryName;
          data['nextCode'] = this.programDetails[this.tempIndex].geo;
          data['nextPrice'] = this.programDetails[this.tempIndex].price;
          data['nextPayout'] = this.programDetails[this.tempIndex].payout;
          data['nextMargin'] = this.programDetails[this.tempIndex].margin;
          this.tempIndex++;
        }

        return data;
      },
      async getCampaignSales() {
        //this.offersLoading = true;
        //this.preloaderChart = true;
        this.chartLoading = true;
        const period = {
          fromDate: DateTime.utc().startOf('day').minus({days: this.chartOptions.chartPeriod}).toSeconds(),
          toDate: DateTime.utc().startOf('day').toSeconds()
        };
        const platform = await affiliates.getPlatform()
        const filter = [{field: 'campaign_id', value: [this.item.data.id]}];
        const params = {
          entity: 'day',
          select: ['day', 'conversions'],
          filter: filter,
          ...period,
          groupBy: 'day',
          orderBy: 'day',
          orderDirectionAsc: false,
          page: 1,
          limit: 100,
          isChart: true,
          platform
        };
        const result = await reports.getDrillDownReports(params);

        const { dates, conversions } = createPeriod(result, this.chartOptions.chartPeriod);

        this.chartOptions = {...this.chartOptions, ...{
            xaxis: {
              categories: dates
            }
          }};

        this.series = [{
          data: conversions
        }];
        this.chartLoading = false
        // this.preloaderChart = false
      },

      addNewCustomOption() {
        this.newCustomOption = {
          optionName: '',
          optionValue: ''
        };
      },

      cancelNewOption() {
        this.newCustomOption = null;
      },

      getValuesForOption(optionName) {
        const option = this.customOptions.find(opt => opt.optionName === optionName);
        return option ? [option.optionValue] : [];
      },

      getAvailableProgramIds(optionName, optionValue) {
        const match = this.customOptions.find(
                opt => opt.optionName === optionName && opt.optionValue === optionValue
        );
        return match && match.availableProgramIds != null
                ? String(match.availableProgramIds)
                : '';
      },

      async getCampaignDetails() {
        const details = await campaigns.getCampaignDetails(this.item.data.id);

        await this.$store.dispatch('offer/saveOfferVariantsStore', this.item.data.offerId);
        this.currentVariant = this.getOfferVariants.find(variant => variant.id === details.variantId)
                || this.getOfferVariants.find(variant => variant.isDefault === true);
        this.isCampaignDetailsLoading = false;
        
        if (details.customOptions) {
            this.allowedPrograms = this.extractProgramIdsFromCustomOptions(details.customOptions);
            const parsedOptions = JSON.parse(details.customOptions);
            this.customOptionsLocal = parsedOptions.map(option => ({
              optionName: option.name,
              optionValue: option.value,
              availableProgramIds: option.programs
            }));
        } else {
            this.customOptionsLocal = [];
            this.allowedPrograms = []; // Reset to allow all programs
        }

        try {
          console.log('OFFER ID', this.item.data.offerId);
          const options = await offerCustomOptionsApi.getOfferCustomOptions(this.item.data.offerId);
          this.customOptions = options;
          console.log('CUSTOM OPTIONS', this.customOptions);
        } catch (error) {
            console.error('Error loading custom options:', error);
        }

        try {
          this.extraParams = JSON.parse(details.sub_ids);
          console.log('EXTRA PARAMS', this.extraParams);
        } catch (e) {
          console.log(e.message);
          this.extraParams = [];
        }
      },

      async getPostbacks() {
        try {
          this.isCampaignPostbackLoading = true;
          const postbacks = await campaigns.campaignPostbacks(this.item.data.id);
          this.paidConversions = postbacks.filter(pb => pb.type === 'paid');
          this.secondaryConversions = postbacks.filter(pb => pb.type === 'secondary');
          console.log('{POSTBACKS}', postbacks);
          this.isCampaignPostbackLoading = false;
        } catch (e) {
          console.log(e.message);
          this.extraParams = [];
        }
      },

      extractProgramIdsFromCustomOptions(customOptions) {
        if (!customOptions) return [];

        let parsed;
        try {
          parsed = typeof customOptions === 'string' ? JSON.parse(customOptions) : customOptions;
        } catch (e) {
          return [];
        }

        if (!Array.isArray(parsed)) return [];

        const uniqueIds = new Set();
        parsed.forEach(option => {
          if (!option || option.programs == null) return;

          const raw = Array.isArray(option.programs) ? option.programs : String(option.programs).split(',');
          raw.forEach(value => {
            const id = parseInt(String(value).trim(), 10);
            if (!isNaN(id)) uniqueIds.add(id);
          });
        });

        return Array.from(uniqueIds);
      },

      async getParams() {
        try {
          const data = this.customOptionsLocal.map(opt => {
            return {
              ...opt,
              programs:opt.availableProgramIds
            }
          })
          const programsFromCustomOptions = this.extractProgramIdsFromCustomOptions(data);

          // Check if custom options have program restrictions
          const hasCustomOptionsWithPrograms = data.some(opt => opt.programs && opt.programs.trim() !== '');
          
          // Only check program compatibility if:
          // 1. Campaign has a program override
          // 2. There are custom options with program restrictions
          // 3. Current program is not in the allowed list
          if (this.hasProgramOverride && this.activeProgramId && hasCustomOptionsWithPrograms && !programsFromCustomOptions.includes(this.activeProgramId)) {
            const proceed = window.confirm('This option isn\'t available with the current program set on this campaign. If you continue the program will reset to the one available on this offer.');
            if (!proceed) {
              return;
            }

            await programs.campaignProgramOverride({
              programId: null,
              oldProgramId: !this.activeProgram ? null : this.activeProgram.id,
              affiliateId: this.item.data.affiliateId,
              campaignId: this.item.data.id,
            })
            this.activeProgram = null;
            this.activeProgramId = null;
            this.hasProgramOverride = false; // Reset override flag
          }
          const selectedOptionIds = this.getCustomOptionsIds().join(',');
          await campaigns.updateCampaignCustomOptions(this.item.data.id, selectedOptionIds);
          this.updating = true;
          const params = JSON.stringify(this.$refs.extraParamsComponent.getLocalParams()).replace(/"/g, "'");
          await campaigns.updateCampaignDetails(this.item.data.id, params, this.currentVariant.id);
          store.dispatch('alert/ALERT_SUCCESS', `Campaign has been updated`, {root: true});
        } catch (e) {
          store.dispatch('alert/ALERT_ERROR', `Update campaign details error. ${e.message}`, { root: true });
        } finally {
          this.updating = false;
        }

      },

      async onUpdate() {
        this.updating = true;

        const updatedData = {
          id: this.item.data.id,
          status: this.status,
          payOut: this.payOut,
          percentPayout: this.percentPayout,
          name: this.name,
          pingRate: this.pingRate,
          postbackUrl: this.postbackUrl,
          affiliateMultiPings: this.affiliateMultiPings
        };

        const result = await store.dispatch('campaigns/updateCampaign', updatedData);

        this.updating = false;

        if (result) {
          await this.fetchPingRate();

          store.dispatch('alert/ALERT_SUCCESS', `Campaign has been updated`, {root: true});
          store.dispatch('cards/REMOVE', `campaign-${this.item.data.id}`)
        } else {
          store.dispatch('alert/ALERT_ERROR', `Campaign update failed due to an error.`, { root: true });
        }

        setTimeout(() => {
          store.dispatch('alert/ALERT_REMOVE', ``, {root: true});
        }, 3000);
      },
      async updateProgram(data) {
        try {

          await programs.campaignProgramOverride({
            programId: this.activeProgramId,
            oldProgramId: !this.activeProgram ? null : this.activeProgram.id,
            affiliateId: data.affiliateId,
            campaignId: data.id
          })

          await this.fetchProgramData();
          // Set override flag since we just created an override
          this.hasProgramOverride = true;
          const programInfo = this.programList.find(value => value.id === this.activeProgramId);
          const updatedData = {
            id: this.item.data.id,
            status: this.status,
            payOut: this.payOut,
            percentPayout: this.percentPayout,
            name: this.name,
            pingRate: this.pingRate,
            programId: this.activeProgramId,
            programName: programInfo.name,
          };
          this.updateCampaignStore(updatedData)

          store.dispatch('alert/ALERT_SUCCESS', `Campaign program updated successfully!`, {root: true});
          store.dispatch('cards/REMOVE', `campaign-${this.item.data.id}`)

        } catch (error) {
          store.dispatch('alert/ALERT_ERROR', error.message, {root: true});
        }

        setTimeout(() => {
          store.dispatch('alert/ALERT_REMOVE', ``, {root: true});
        }, 3000);
      },
      updStart(date) {
        this.caps.startDate = date;
      },
      updEnd(date) {
        this.caps.endDate = date;
      },
      async updateCaps() {

        const updCap = {
          id: this.caps.id,
          campaignId: this.item.data.id,
          clicksDay: this.caps.clicksDay,
          clicksWeek: this.caps.clicksWeek,
          clicksMonth: this.caps.clicksMonth,
          clicksRedirectOfferId: this.caps.clicksRedirectOfferId ? this.caps.clicksRedirectOfferId.id : null,
          salesDay: this.caps.salesDay,
          salesWeek: this.caps.salesWeek,
          salesMonth: this.caps.salesMonth,
          salesRedirectOfferId: this.caps.salesRedirectOfferId ? this.caps.salesRedirectOfferId.id : null,
          startDate: this.caps.startDate,
          endDate: this.caps.endDate,
          enabled: this.caps.isCapsEnabled
        };

        const result = await store.dispatch('campaigns/updateCap', updCap);

        if (result) {
          store.dispatch('alert/ALERT_SUCCESS', `Cap campaign has been updated`, {root: true});
          store.dispatch('cards/REMOVE', `campaign-${this.item.data.id}`)
        } else {
          store.dispatch('alert/ALERT_ERROR', `Error updating the campaign cap.`, { root: true });
        }

        setTimeout(() => {
          store.dispatch('alert/ALERT_REMOVE', ``, {root: true});
        }, 3000);
      },
      ...mapMutations('campaigns', ['updateCampaignStore']),
      download() {

        const geos = [];

        if (!this.preparedProgramsForTable || !Array.isArray(this.preparedProgramsForTable)) {
          return;
        }
        this.preparedProgramsForTable.forEach(item => {
          geos.push({
            geo: item.geo,
            payout: item.payout
          });
        });

        download(geos);
      },
      addPaid() {
        this.paidConversions.push({ url: "" });
      },
      removePaid(index) {
        this.paidConversions.splice(index, 1);
      },
      addSecondary() {
        this.secondaryConversions.push({ url: "", event: "click" });
      },
      removeSecondary(index) {
        this.secondaryConversions.splice(index, 1);
      },
      async updatePostbacks() {
        this.isUpdating = true;
        const campaignId = this.item.data.id;

        this.paidConversions.forEach(pb => pb.type = 'paid');

        const postbacks = [...this.paidConversions, ...this.secondaryConversions].map(pb => ({
          id: pb.id || null,
          url: pb.postback,
          event: pb.event || "click",
          type: pb.type || "secondary",
        }));

        try {
          const response = await campaigns.updateCampaignPostbacks({ campaignId, postbacks });

          if (response) {
            store.dispatch('alert/ALERT_SUCCESS', `Postbacks have been successfully saved`, { root: true });
          } else {
            store.dispatch('alert/ALERT_ERROR', `An error occurred while saving postbacks`, { root: true });
          }
        } catch (e) {
          console.log(e);
          store.dispatch('alert/ALERT_ERROR', `An error occurred while saving postbacks`, { root: true });
        } finally {
          this.isUpdating = false;
        }

      }
    
    },
    name: "CampaignCard",
    data() {
      return {
        allowedPrograms: [],
        customOptions: [],
        customOptionsLocal: [],
        availableOptions: ['Trial Type1', 'Color'],
        newCustomOption: null,
        hasProgramOverride: false,
        paidConversions: [],
        secondaryConversions: [],
        historyHeaders: [
          {text: 'User', align: 'start', sortable: false, value: 'user'},
          {text: 'Action', sortable: false, value: 'action'},
          {text: 'Date', sortable: false, value: 'dateAdded'},
          {text: 'Logs', sortable: false, value: 'logs'},
        ],
        historyData: [],
        isLogsLoading: true,
        isCampaignDetailsLoading: true,
        isCampaignPostbackLoading: false,
        isUpdating: false,
        currentVariant: null,
        extraParams: [],
        activeTabId: 0,
        isMounted: false,
        rules: [
          v => /^\d+$/.test(v) || 'Must be a positive number',
        ],
        pingRateRules: [
          v => /^[0-9][0-9]?$|^100$/.test(v) || 'Must be a number between 0 and 100'
        ],
        caps: {
          id: this.item.data.capId,
          isCapsEnabled: this.item.data.enabled,
          salesDay: this.item.data.sales_day,
          salesWeek: this.item.data.sales_week,
          salesMonth: this.item.data.sales_month,
          salesRedirectOfferId: this.item.data.sales_redirect_offer_id,
          clicksDay: this.item.data.clicks_day,
          clicksWeek: this.item.data.clicks_week,
          clicksMonth: this.item.data.clicks_month,
          clicksRedirectOfferId: this.item.data.clicks_redirect_offer_id,
          startDate: this.item.data.startDate,
          endDate: this.item.data.endDate,
          useDates: this.item.data.startDate || this.item.data.endDate
        },
        programList: [],
        programDetails: [],
        isProgramLoading: true,
        allowedCountries: [],
        isAllCountriesAllowed: false,
        activeProgram: null,
        offers: [],
        chartLoading: true,
        payOut: this.item.data.payOut,
        percentPayout: this.item.data.percentPayout,
        status: this.item.data.status,
        name: this.item.data.name,
        postbackUrl: this.item.data.postbackUrl,
        affiliateMultiPings: this.item.data.affiliateMultiPings || null,
        pingRate: null,
        pingRateCached: null,
        updating: false,
        programUpdating: false,
        activeProgramId: null,
        tempIndex: 0,
        chartOptions: {
          chartPeriod: 7,
          chart: {
            id: `campaign-${this.item.data.id}-chart`,
            toolbar: {
              tools: {
                download: false,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false
              }
            },
          },
          xaxis: {
            categories: []
          },
          colors: [this.$vuetify.theme.themes.light.primary],
        },
        series: [
          {
            name: 'Conversions',
            data: []
          },
        ],
      }
    }
  }
</script>

<style lang="sass">
.new-program
  height: fit-content

.jccenter
  justify-content: center
  margin-top: 1%
  min-height: 350px !important
.extra
  margin-top: 3%
  margin-bottom: 5%

#fixed-tabs .v-tabs-bar
   position: fixed
   z-index: 1
   height: 45px
   width: 100%

#fixed-tabs .v-tab-item
  margin-top: 50px

.download
  margin-top: 30px
  margin-left: 15px
  &:hover
    cursor: pointer

.title
  font-size: 20px
  font-weight: 600
  margin-bottom: 10px
  color: #717171

.postback-label
  display: block
  margin-bottom: 10px
  font-size: 18px
  font-weight: bold
  color: var(--v-primary-base)

.select-label
  display: block
  margin-bottom: 10px
  font-size: 18px
  font-weight: bold
  color: #717171

.conversion-item
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 8px


.select
  padding: 6px
  border: 1px solid #ccc
  border-radius: 4px


.remove
  padding-top: 30px !important
  &:hover
    cursor: pointer

</style>

